import React, { useState, createElement } from 'react';
import { Button } from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Eye, EyeOff, Instagram } from 'lucide-react';
// Ghost icon for Snapchat
const GhostIcon = ({
  className
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-273">
    <path d="M12 2C8.5 2 6 4.5 6 8c0 2 0 4-2 6-2 2-2 3 0 3 1 0 2 0 3 1 1 1 2 2 5 2s4-1 5-2c1-1 2-1 3-1 2 0 2-1 0-3-2-2-2-4-2-6 0-3.5-2.5-6-6-6z" data-id="element-274" />
    <circle cx="9" cy="9" r="1" fill="currentColor" data-id="element-275" />
    <circle cx="15" cy="9" r="1" fill="currentColor" data-id="element-276" />
  </svg>;
export function ViralRecap() {
  const [isSoftLaunch, setIsSoftLaunch] = useState(false);
  const handleShare = async (platform: 'instagram' | 'snapchat') => {
    // Create a canvas to generate an image with the FlingLink icon
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#ff6b6b');
      gradient.addColorStop(1, '#ff8e8e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Add FlingLink icon
      const icon = new Image();
      icon.crossOrigin = 'anonymous';
      icon.src = 'https://cdn.magicpatterns.com/uploads/1bbXaGPHqQh3nL8ey2LPuZ/FlingLink-Icon.png';
      icon.onload = () => {
        // Draw icon at top
        ctx.drawImage(icon, 40, 40, 120, 120);
        // Add text
        ctx.fillStyle = 'white';
        ctx.font = 'bold 48px sans-serif';
        ctx.fillText('FLINGLINK RECAP', 40, 220);
        ctx.font = '36px sans-serif';
        ctx.fillText(isSoftLaunch ? 'Me & ???' : 'Felicia & Aneka', 40, 280);
        // Convert to blob and share
        canvas.toBlob(async blob => {
          if (blob) {
            const file = new File([blob], 'flinglink-recap.png', {
              type: 'image/png'
            });
            if (navigator.share && navigator.canShare({
              files: [file]
            })) {
              try {
                await navigator.share({
                  files: [file],
                  title: 'FlingLink Recap',
                  text: isSoftLaunch ? 'My FlingLink Week 1 Recap 🤫' : 'Felicia & Aneka - FlingLink Week 1 Recap',
                  url: 'https://flingl.ink'
                });
              } catch (err) {
                console.log('Share cancelled or failed');
              }
            } else {
              // Fallback: Open platform-specific URLs
              if (platform === 'instagram') {
                window.open('https://www.instagram.com/', '_blank');
              } else if (platform === 'snapchat') {
                window.open('https://www.snapchat.com/', '_blank');
              }
            }
          }
        });
      };
      // Fallback if image doesn't load
      icon.onerror = () => {
        if (platform === 'instagram') {
          window.open('https://www.instagram.com/', '_blank');
        } else if (platform === 'snapchat') {
          window.open('https://www.snapchat.com/', '_blank');
        }
      };
    }
  };
  return <section className="py-20 overflow-hidden" style={{
    background: 'linear-gradient(to bottom right, #ffe5e5, #fff0f5)'
  }} data-id="element-277">
      <div className="container mx-auto px-4" data-id="element-278">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-279">
          <div className="flex-1 w-full" data-id="element-280">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-281">
              Soft Launch Mode
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-8" data-id="element-282">
              Post it. Or don't. Share your weekly recap without exposing
              anyone. Toggle "Soft Launch" to blur names and photos.
            </p>

            <div className="flex items-center gap-4 mb-8" data-id="element-283">
              <span className={`font-medium ${!isSoftLaunch ? 'text-black' : 'text-gray-400'}`} data-id="element-284">
                Public
              </span>
              <button onClick={() => setIsSoftLaunch(!isSoftLaunch)} className={`w-16 h-8 rounded-full p-1 transition-colors`} style={{
              backgroundColor: isSoftLaunch ? '#ff6b6b' : '#d1d5db'
            }} data-id="element-285">
                <motion.div className="w-6 h-6 bg-white rounded-full shadow-sm" animate={{
                x: isSoftLaunch ? 32 : 0
              }} data-id="element-286" />
              </button>
              <span className={`font-medium ${isSoftLaunch ? 'text-black' : 'text-gray-400'}`} style={{
              color: isSoftLaunch ? '#ff6b6b' : undefined
            }} data-id="element-287">
                Soft Launch 🤫
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-288">
              <Button variant="outline" className="gap-2 w-full sm:w-auto" onClick={() => handleShare('instagram')} data-id="element-289">
                <Instagram className="w-4 h-4" data-id="element-290" /> Share to Story
              </Button>
              <Button variant="outline" className="gap-2 w-full sm:w-auto" onClick={() => handleShare('snapchat')} data-id="element-291">
                <GhostIcon className="w-4 h-4" data-id="element-292" /> Share to Snap
              </Button>
            </div>
          </div>

          <div className="flex-1 w-full flex justify-center lg:justify-end" data-id="element-293">
            <motion.div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm rotate-3 border-8 border-white" animate={{
            rotate: isSoftLaunch ? -2 : 3
          }} data-id="element-294">
              <div className="rounded-2xl p-6 text-white h-[420px] flex flex-col relative overflow-hidden" style={{
              background: 'linear-gradient(to bottom right, #ff6b6b, #ff8e8e)'
            }} data-id="element-295">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-296"></div>

                <div className="relative z-10 flex flex-col h-full" data-id="element-297">
                  <div className="flex justify-between items-start mb-8" data-id="element-298">
                    <div className="flex items-center gap-2" data-id="element-299">
                      <img src="https://cdn.magicpatterns.com/uploads/1bbXaGPHqQh3nL8ey2LPuZ/FlingLink-Icon.png" alt="FlingLink" className="w-8 h-8" data-id="element-300" />
                      <div className="text-xs font-bold tracking-widest opacity-70" data-id="element-301">
                        FLINGLINK RECAP
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded text-xs font-bold" data-id="element-302">
                      WEEK 1
                    </div>
                  </div>

                  <div className="text-center mb-8" data-id="element-303">
                    <div className="flex justify-center -space-x-4 mb-4" data-id="element-304">
                      <div className="w-16 h-16 rounded-full border-4 bg-gray-200 overflow-hidden" style={{
                      borderColor: '#ff6b6b'
                    }} data-id="element-305">
                        <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felicia" alt="Felicia" data-id="element-306" />
                      </div>
                      <div className="w-16 h-16 rounded-full border-4 bg-gray-200 overflow-hidden relative" style={{
                      borderColor: '#ff6b6b'
                    }} data-id="element-307">
                        <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka" alt="Aneka" data-id="element-308" />
                        <AnimatePresence data-id="element-309">
                          {isSoftLaunch && <motion.div initial={{
                          opacity: 0
                        }} animate={{
                          opacity: 1
                        }} exit={{
                          opacity: 0
                        }} className="absolute inset-0 backdrop-blur-sm flex items-center justify-center" style={{
                          backgroundColor: 'rgba(255, 107, 107, 0.8)'
                        }} data-id="element-310">
                              <EyeOff className="w-6 h-6 text-white/50" data-id="element-311" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="relative h-8" data-id="element-312">
                      <AnimatePresence mode="wait" data-id="element-313">
                        {isSoftLaunch ? <motion.div key="hidden" initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className="font-bold text-xl" data-id="element-314">
                            Me & ???
                          </motion.div> : <motion.div key="visible" initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className="font-bold text-xl" data-id="element-315">
                            Felicia & Aneka
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="space-y-3 flex-1" data-id="element-316">
                    <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl flex justify-between items-center" data-id="element-317">
                      <span className="text-sm opacity-90" data-id="element-318">Vibe Check</span>
                      <span className="font-bold" data-id="element-319">Immaculate ✨</span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl flex justify-between items-center" data-id="element-320">
                      <span className="text-sm opacity-90" data-id="element-321">Streak</span>
                      <span className="font-bold" data-id="element-322">7 Days 🔥</span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl flex justify-between items-center" data-id="element-323">
                      <span className="text-sm opacity-90" data-id="element-324">Status</span>
                      <span className="font-bold bg-white px-2 py-0.5 rounded text-xs uppercase" style={{
                      color: '#ff6b6b'
                    }} data-id="element-325">
                        Renewed
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/20 text-center" data-id="element-326">
                    <a href="https://me.flinglink.app/signup" className="text-sm font-bold opacity-90 hover:opacity-100 transition-opacity" data-id="element-327">
                      Get your FlingLink →
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}